import React, { useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

const COPIED_FEEDBACK_MS = 1600;

const inlineCodeClassName =
  'break-all rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-rose-600 dark:bg-slate-900 dark:text-rose-300';

function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }
  return '';
}

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const codeElement = React.Children.toArray(children).find(React.isValidElement);
  const className = React.isValidElement<{ className?: string }>(codeElement)
    ? codeElement.props.className
    : '';
  const language = className?.match(/language-([\w-]+)/)?.[1] || 'text';
  const code = useMemo(() => getTextContent(children).replace(/\n$/, ''), [children]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      })
      .catch(() => undefined);
  };

  return (
    <div className="mb-3 overflow-hidden rounded-md border border-slate-700 bg-slate-950 text-slate-100 last:mb-0">
      <div className="flex min-h-9 items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-3 py-1.5">
        <span className="truncate font-mono text-xs text-slate-300">{language}</span>
        <button
          type="button"
          onClick={handleCopy}
          title="Copy code"
          aria-label="Copy code"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto p-3 text-[13px] leading-relaxed">{children}</pre>
    </div>
  );
};

/** Same element styling as the user chat's MarkdownRenderer (aife), minus citations/images. */
const components: Components = {
  h1: ({ children, ...props }) => (
    <h1 className="mb-2 mt-3 text-xl font-semibold leading-snug first:mt-0" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="mb-2 mt-3 text-lg font-semibold leading-snug first:mt-0" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="mb-1.5 mt-3 text-base font-semibold leading-snug first:mt-0" {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p className="mb-2 last:mb-0" {...props}>
      {children}
    </p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="pl-1 leading-relaxed" {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="mb-2 border-l-2 border-gray-300 pl-3 text-gray-600 last:mb-0 dark:border-slate-600 dark:text-slate-300"
      {...props}
    >
      {children}
    </blockquote>
  ),
  a: ({ children, ...props }) => (
    <a
      className="font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-700 dark:text-sky-300 dark:decoration-sky-500/60 dark:hover:text-sky-200"
      target="_blank"
      rel="noopener noreferrer"
      title={typeof props.href === 'string' ? props.href : undefined}
      {...props}
    >
      {children}
    </a>
  ),
  // Model output may echo untrusted data: an auto-loaded image URL could leak it to a third party.
  img: ({ alt }) => (
    <span className="italic text-slate-500 dark:text-slate-400">
      [image{alt ? `: ${alt}` : ''}]
    </span>
  ),
  table: ({ children, ...props }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table
        className="min-w-full border-collapse overflow-hidden rounded-md border border-gray-200 text-left text-sm dark:border-slate-700"
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-gray-50 dark:bg-slate-900/70" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }) => (
    <th
      className="border border-gray-200 px-2.5 py-1.5 font-semibold dark:border-slate-700"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-gray-200 px-2.5 py-1.5 align-top dark:border-slate-700" {...props}>
      {children}
    </td>
  ),
  code: ({ className, children, ...props }) => (
    <code className={className ? `${className} font-mono` : inlineCodeClassName} {...props}>
      {children}
    </code>
  ),
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
};

const rehypePlugins = [rehypeSanitize, rehypeHighlight];

export const AdminMarkdown: React.FC<{ text: string; className?: string }> = ({
  text,
  className = '',
}) => (
  <div className={`markdown-renderer min-w-0 break-words [overflow-wrap:anywhere] ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {text}
    </ReactMarkdown>
  </div>
);
